<template>
	<b-form-group
		:class="{required: required, 'is-col': isCol, 'disabled': disabled}"
		:label="title"
		v-click-outside="close"
	>
		<div class="detail-btns">
			<slot name="detail-btns" />
		</div>

		<!-- 메모 -->
		<b-textarea
			v-if="type === 'textarea'"
			no-resize
			rows="5"
			:value="formatValue(value)"
			@input="onInput"
			:placeholder="placeholder ? placeholder : format.placeholder(title)"
			:state="validation"
			:name="name"
			autocomplete="off"
		/>

		<!-- 검색 + 입력 가능 -->
		<div class="form-search" v-else-if="type === 'search'" :class="{right: dropright}">
			<b-input-group ref="search-anchor">
				<b-form-input
					:value="value"
					:formatter="formatter"
					@input="onInput"
					@focus="onSearchFocus"
					@mousedown.native="onSearchMouseDown"
					@click.native="onSearchClick"
					@keyup.enter.native="onSearchEnter"
					@blur="close"
					size="lg"
					:name="name"
					autocomplete="off"
					:placeholder="placeholder ? placeholder : format.placeholder(title)"
					:state="validation"
					:disabled="disabled"
				/>
				<slot name="close"/>
			</b-input-group>
			<b-list-group
				ref="search-options"
				:class="{active: showOption}"
				:style="floatingStyle"
			>
				<b-list-group-item
					button
					v-for="(option,index) in options"
					:key="`search-${option.value}-${index}`"
					@mousedown.prevent="option.disabled ? null : onSelect(option)"
					:class="{disabled: option.disabled}"
					v-show="setSearchResult(option, value)"
				>	
					<p v-html="format.highlighted(option.text, value)"/>
				</b-list-group-item>
			</b-list-group>
		</div>

		<!-- 선택 전용 -->
		<b-dropdown class="form-select" v-else-if="type === 'select'" :dropright="dropright">
			<template #button-content>
				<b-form-input 
					readonly 
					:placeholder="placeholder ? placeholder : format.placeholder(title)"
					:value="selectDisplayLabel"
					:state="validation"
					size="lg"
					:disabled="disabled"
				/>
			</template>
			<b-dropdown-group>
				<b-dropdown-item-button
					v-for="(option,index) in options"
					:key="`select-${option.name}${index}`"
					@click="option.disabled ? null : onSelect(option)"
					:class="{disabled: option.disabled}"
				>
					{{ option.text }}
				</b-dropdown-item-button>
			</b-dropdown-group>
		</b-dropdown>

		<!-- 멀티 셀렉트 -->
		<div 
			class="form-multi-select"  
			v-else-if="type === 'multi-select'" 
			:class="{right: dropright, disabled: disabled}"
		>
			<b-input-group @click="e => !disabled && $refs['multi-input'].focus()">
				<div class="badges">
					<b-badge
						class="badge"
						v-for="(item, index) in values"
						:key="`multi-${selectBy}-${item}-${index}`"
					>
						<template v-if="selectBy === 'value'">{{ multiSelectLabelForValue(item) }}</template>
						<template v-else>{{ item }}</template>
						<button v-if="!disabled" type="button" class="close" @click="deleteValues(item)"/>
					</b-badge>
					<b-form-input
						ref="multi-input"
						@focus="!disabled && (showOption = true)"
						@click="!disabled && (showOption = true)"
						@input="onInput"
						v-model="multiSelectValue"
						:name="name"
						autocomplete="off"
						:disabled="disabled"
						:readonly="readonly || disabled"
						@keyup.enter="e => addValues(e)"
					/>
				</div>
				<button v-if="!disabled" type="button" class="select-arrow" @click="showOption = !showOption"></button>
			</b-input-group>
			<b-list-group v-if="!disabled" :class="{active: showOption}">
				<b-list-group-item
					button
					v-for="(option,index) in options"
					:key="`search-${option.value}-${index}`"
					class="check-item custom-control custom-checkbox"
					v-show="setSearchResult(option, multiSelectValue)"
				>	
					<input
						type="checkbox"
						class="custom-control-input"
						@change="e => onChecked(e, option)"
						:id="`option-${formKey}-${index}`"
						:checked="isMultiSelectChecked(option)"
						:value="option.value"
					/>
					<label class="custom-control-label" :for="`option-${formKey}-${index}`">
						<slot name="multiSelectOptionLabel" :option="option" :index="index">
							{{ option.text }}
						</slot>
					</label>
				</b-list-group-item>
			</b-list-group>
		</div>

		<!-- 날짜 -->
		<b-input-group v-else-if="type === 'date'" class="form-datepicker">
			<b-form-datepicker
				button-only
				v-bind="bFormDatepickerLabels"
				:value="datePickerValue"
				:locale="datepickerLocale"
				:date-format-options="{ year: 'numeric', month: '2-digit', day: '2-digit' }"
				hide-header
				size="lg"
				:state="validation"
				:disabled="disabled"
				@input="onDatepickerInput"                      
				@context="onDatepickerContext"
			/>
			<b-form-input
				:value="textInputValue"
				type="text"
				:name="name"
				autocomplete="off"
				size="lg"
				:placeholder="placeholder ? placeholder : format.placeholder(title)"
				:state="validation"
				:disabled="disabled"
				@input="onDateTextInput"
				style="display: block;"
			/>
			<img 
				v-if="value"
				class="date-close-btn" 
				@click="onReset" 
				src="@/assets/img/close.png"
			>
		</b-input-group>

		<!-- 스위치 -->
		<b-input-group v-else-if="(type == 'group' || type == 'test')">
			<div>
				<b-form-checkbox 
					size="lg" 
					:checked="checked" 
					:disabled="disabled"
					switch 
					@change="value => $emit('change', value)"
				/>
			</div>
		</b-input-group>

		<!-- 일반 텍스트 -->
		<b-input-group v-else>
			<b-form-input
				size="lg"
				:value="value"
				:type="formatType"
				:readonly="readonly"
				@input="onInput"
				@keydown="e => $emit('keydown', e)"
				@blur="onBlur"
				:placeholder="placeholder ? placeholder : format.placeholder(title)"
				:formatter="formatter"
				:state="validation"
				:disabled="disabled"
				:name="name"
				:autocomplete="autocomplete"
			/>
			<span class="unit" v-if="type === 'amount'">{{ unit }}</span>
			<input
				v-else-if="showUnitInput || unit"
				class="unit"
				type="text"
				:value="unit"
				@input="onUnitInput"
				style="width: 30px;"
			/>
		</b-input-group>

		<b-form-invalid-feedback>
			{{ placeholder ? placeholder : format.placeholder(title) }}
		</b-form-invalid-feedback>
	</b-form-group>
</template>

<script>
import format from '@/utils/format'
import {
	DATEPICKER_LOCALE,
	B_FORM_DATEPICKER_LABELS
} from '@/utils/datepickerLocale'
import Vue from 'vue'

Vue.directive('click-outside', {
	bind: function (el, binding, vnode) {
		el.clickOutsideEvent = function (event) {
			if (!(el == event.target || el.contains(event.target))) {
				vnode.context[binding.expression](event);
			}
		};
		document.body.addEventListener('click', el.clickOutsideEvent)
	},
	unbind: function (el) {
		document.body.removeEventListener('click', el.clickOutsideEvent)
	},
});

export default {
	name: 'Fields',
	props: {
		title: { 
			type: String, 
			default: ''
		},
		value: { 
			type: null, 
			default: () => ''
		},
		required: { 
			type: Boolean, 
			default: false
		},
		type: { 
			type: String, 
			default: 'text'
		},
		placeholder: { 
			type: String, 
			default: ''
		},
		isCol: { 
			type: Boolean, 
			default: false
		},
		readonly: { 
			type: Boolean, 
			default: false
		},
		/**
		 * 금액 입력칸에서 음수를 허용한다 (type="amount" · "number" 에만 의미가 있다).
		 * 기본은 false — 기존 칸의 동작을 바꾸지 않는다.
		 */
		allowNegative: {
			type: Boolean,
			default: false
		},
		options: { 
			type: Array, 
			default: () => []
		},
		unit: { 
			type: String, 
			default: ''
		},
		/** 단위 문자열이 비어 있어도 단위 입력칸 표시 (시험일지 optional 사용량 등) */
		showUnitInput: {
			type: Boolean,
			default: false
		},
		selected: { 
			type: Array, 
			default: () => []
		},
		checked: { 
			type: Boolean, 
			default: false
		},
		isValidate: { 
			type: Boolean, 
			default: false
		},
		dropright: { 
			type: Boolean, 
			default: false
		},
		disabled: { 
			type: Boolean, 
			default: false
		},
		formKey: { 
			type: String, 
			default: '' 
		},
		/** multi-select: 'text' 기본(이름으로 선택). 'value' = option.value(id)로 구분(동명이인) */
		selectBy: {
			type: String,
			default: 'text',
			validator: (v) => ['text', 'value'].includes(v),
		},
		/** true면 option.sub 가 있을 때 동명이인 여부와 무관하게 배지에 (sub) 표기 */
		alwaysShowOptionSub: {
			type: Boolean,
			default: false,
		},
		/**
		 * search: 옵션 목록을 뷰포트 기준(position: fixed)으로 띄운다.
		 * 기본값 absolute 는 스크롤 조상(overflow:auto)에 잘리므로, 높이가 좁은
		 * 컨테이너 안에 검색칸이 있을 때만 켠다. 켜는 쪽이 책임지고 검증할 것.
		 */
		floating: {
			type: Boolean,
			default: false,
		},
		name: {
			type: String,
			default: ''
		},
		autocomplete: {
			type: String,
			default: 'off'
		},
	},
	data() {
		return {
			format,
			datepickerLocale: DATEPICKER_LOCALE,
			bFormDatepickerLabels: B_FORM_DATEPICKER_LABELS,
			showOption: false,
			searchInputWasFocused: false,
			closeOptionTimer: null,
			multiSelectValue: '',
			isTyping: false,          // 수동 입력 플래그
			displayMode: 'hyphen',    // 'hyphen' | 'digits'
			dateDraft: '',
			floatingStyle: null,      // floating 옵션 목록의 뷰포트 좌표
		}
	},
	created() {
		// 초기 렌더 시: 외부 값이 있으면 기본은 하이픈 모드
		if (this.value && this.type === 'date') {
			this.displayMode = 'hyphen';
		}
	},
	computed: {
		validation() {
			return this.required ? this.isValidate : true;
		},
		formatType() {
			return (this.type === 'phone' || this.type === 'amount') ? 'text' : this.type;
		},
		/** select: 읽기전용 표시값은 코드(value)가 아니라 옵션의 사용자용 text */
		selectDisplayLabel() {
			if (this.type !== 'select') return '';
			const v = this.value;
			if (v === null || v === undefined || v === '') return '';
			const row = (this.options || []).find((o) => String(o.value) === String(v));
			return row && row.text != null ? String(row.text) : String(v);
		},
		values() {
			if (this.type === 'multi-select') {
				if (this.selected && this.selected.length > 0) {
					if (this.selectBy === 'value') {
						return [...this.selected].map((s) => String(s).trim())
					}
					return [...this.selected]
				}
				if (this.value) {
					if (this.selectBy === 'value') {
						return this.value
							.split(',')
							.map((s) => s.trim())
							.filter((s) => s !== '')
					}
					return this.value.split(',')
				}
				return []
			}
		},

		// 달력에는 항상 YYYY-MM-DD로 전달
		datePickerValue() {
			if (this.type !== 'date') return this.value;
			return this.toHyphen(this.value);
		},
		// 텍스트 인풋은 모드에 따라 표시
		textInputValue() {
			if (this.displayMode === 'digits') {
				return this.dateDraft;
			}
			return this.toHyphen(this.value);
		},
	},
	watch: {
		value: {
			immediate: true,
			handler(newVal) {
				if (this.type !== 'date') return;
				// 수동 입력 중이면 외부 갱신으로 표시 모드가 바뀌지 않도록
				if (this.isTyping) return;
				// 외부 변경/초기값/달력 선택은 기본적으로 하이픈 표시
				this.displayMode = 'hyphen';
				this.dateDraft = '';
			}
		},
		showOption(open) {
			if (!this.floating || this.type !== 'search') return;
			// 목록이 그려진 뒤라야 폭·높이를 잴 수 있다
			if (open) this.$nextTick(this.startFloating);
			else this.stopFloating();
		},
	},
	beforeDestroy() {
		this.stopFloating();
	},
	methods: {
		// ---------- 공통 ----------
		formatValue(val) {
			if (Array.isArray(val) || typeof val === 'object') {
				if (!val) return null;
				return JSON.stringify(val, null, 2);
			}
			return val;
		},
		onInput(value) {
			if (this.type !== 'date') {
				if (this.type === 'search') {
					this.openSearchOptions();
				}
				this.$emit('input', value);
			}
		},
		clearCloseOptionTimer() {
			if (this.closeOptionTimer) {
				clearTimeout(this.closeOptionTimer);
				this.closeOptionTimer = null;
			}
		},
		openSearchOptions() {
			this.clearCloseOptionTimer();
			this.showOption = true;
		},
		// ---------- search: floating 옵션 목록 ----------
		/**
		 * 조상의 overflow 에 잘리지 않도록 목록을 뷰포트 기준으로 띄운다.
		 * position:fixed 는 조상에 transform/filter/will-change/contain 이 있으면
		 * 그 조상 기준으로 잡혀 무력화되니, floating 을 켜는 화면은 그 점을 확인할 것.
		 */
		startFloating() {
			this.updateFloatingPosition();
			// 캡처 단계라야 window 가 아닌 중간 스크롤 컨테이너의 스크롤까지 잡는다
			window.addEventListener('scroll', this.updateFloatingPosition, true);
			window.addEventListener('resize', this.updateFloatingPosition);
		},
		stopFloating() {
			window.removeEventListener('scroll', this.updateFloatingPosition, true);
			window.removeEventListener('resize', this.updateFloatingPosition);
			this.floatingStyle = null;
		},
		updateFloatingPosition() {
			const anchor = this.$refs['search-anchor'];
			const el = anchor && (anchor.$el || anchor);
			if (!el) return;

			const rect = el.getBoundingClientRect();
			const GAP = 4;      // 입력칸과 목록 사이
			const EDGE = 8;     // 화면 가장자리 여백
			const MAX = 250;    // form.scss 의 기본 max-height 와 맞춘다
			const MIN = 120;    // 이보다 낮으면 목록 구실을 못 한다

			const below = window.innerHeight - rect.bottom - GAP - EDGE;
			const above = rect.top - GAP - EDGE;
			// 아래가 좁고 위가 더 넓을 때만 뒤집는다
			const flipUp = below < Math.min(MAX, MIN) && above > below;
			const room = Math.max(flipUp ? above : below, MIN);

			const base = {
				position: 'fixed',
				left: `${rect.left}px`,
				width: `${rect.width}px`,
				maxHeight: `${Math.min(MAX, room)}px`,
				// 기본 transition 이 all 이라 스크롤 재배치마다 top/left 가 애니메이션돼
				// 목록이 입력칸을 뒤늦게 쫓아온다. 페이드만 남긴다.
				transition: 'opacity 0.3s ease',
			};
			this.floatingStyle = flipUp
				? { ...base, top: 'auto', bottom: `${window.innerHeight - rect.top + GAP}px` }
				: { ...base, top: `${rect.bottom + GAP}px`, bottom: 'auto' };
		},
		isSearchInputFocused() {
			const input = this.$el && this.$el.querySelector('.form-search input.form-control');
			return !!(input && document.activeElement === input);
		},
		onSearchFocus() {
			this.openSearchOptions();
		},
		onSearchMouseDown() {
			this.searchInputWasFocused = this.isSearchInputFocused();
		},
		onSearchClick() {
			if (this.searchInputWasFocused) {
				this.openSearchOptions();
			}
		},
		onSearchEnter(e) {
			if (e) e.preventDefault();
			this.clearCloseOptionTimer();
			this.showOption = false;
			this.$emit('enter', e);
		},
		onReset() {
			this.$emit('input', '');
			this.$emit('reset');
			this.isTyping = false;
			this.displayMode = 'hyphen';
		},
		onBlur(e) {
			// 숫자 자동변환시 마지막 글자 "." 인경우 예외처리 
			if (this.value && !isNaN(Number(this.value)) && this.value?.toString().charAt(this.value.length - 1) !== '.') {
				this.$emit('input', String(this.value))
			}
		},
		// ---------- multiSelect ----------
		isMultiSelectChecked(option) {
			if (this.type !== 'multi-select') return false
			if (this.selectBy === 'value') {
				return this.values.map(String).includes(String(option.value))
			}
			return (this.selected || []).includes(option.text)
		},
		multiSelectLabelForValue(idToken) {
			const o = (this.options || []).find(
				(x) => String(x.value) === String(idToken),
			);
			if (!o) return idToken;
			const hasSub = o.sub != null && String(o.sub) !== '';
			// 폐기물: 동명 자식(발열량)도 부모명 괄호 표기 — sub===text 여부와 무관
			if (this.alwaysShowOptionSub && hasSub) {
				return `${o.text} (${o.sub})`;
			}
			// 동일 라벨 옵션이 여럿이면(폐기물 TCE/PCE·수분 등) 배지에 그룹(sub)을 붙여 구별
			const hasDistinctSub = hasSub && o.sub !== o.text;
			const hasDuplicateText = (this.options || []).filter((x) => x.text === o.text).length > 1;
			return hasDuplicateText && hasDistinctSub ? `${o.text} (${o.sub})` : o.text;
		},
		onChecked(e, option) {
			if (this.selectBy === 'value') {
				const idStr = String(option.value);
				const arr = [...this.values];
				let result;
				if (e.target.checked) {
					result = [...arr, idStr];
				} else {
					result = arr.filter((el) => String(el) !== idStr);
				}
				this.multiSelectValue = '';
				if (this.$refs['multi-input']) this.$refs['multi-input'].focus();
				this.$emit('change', this.formKey, result.join(','), option.text, option);
				return;
			}
			const arr = [...this.values];
			let result = [];
			if (e.target.checked) {
				result = [...arr, ...[option.text]];
			} else {
				result = arr.filter((el) => el !== option.text);
			}
			this.multiSelectValue = '';
			this.$refs['multi-input'].focus();
			this.$emit('change', this.formKey, result.join(','), option.text);
		},
		onSelect(option) {
			this.$emit('select', option);
			this.clearCloseOptionTimer();
			this.showOption = false;
		},
		formatter(value) {
			if (this.type == 'phone') {
				return format.phone(value);
			} else if (this.type === 'search') {
				if (typeof value == 'object') return value.text;
				return value;
			} else if (this.type == 'amount' || this.type == 'number') {
				// allowNegative 인 칸만 음수 허용 포맷터를 쓴다.
				// format.number 는 '-' 를 NaN 으로 바꿔 음수 입력을 막는다(format.js 주석 참조).
				return this.allowNegative ? format.signedAmount(value) : format.number(value);
			}
			return value;
		},
		close() {
			this.clearCloseOptionTimer();
			this.closeOptionTimer = setTimeout(() => {
				this.showOption = false;
				this.closeOptionTimer = null;
			}, 200);
		},
		deleteValues(val) {
			const result = this.values.filter(
				(el) => String(el) !== String(val),
			);
			this.$emit('change', this.formKey, result.join(','), val)
			this.showOption = true;
		},
		addValues(e) {
			if (this.selectBy === 'value') {
				this.multiSelectValue = '';
				const q = (e.target.value || '').trim();
				if (!q) {
					return;
				}
				const parts = q.includes(',')
					? q.split(',').map((p) => p.trim()).filter(Boolean)
					: [q];
				const next = [...this.values];
				const seen = new Set(next.map((v) => String(v)));
				const addPart = (part) => {
					if (!part) return;
					const sub = this.options.filter(
						(el) =>
							(el.text &&
								format
									.lower(el.text)
									.indexOf(format.lower(part)) >= 0) ||
							(el.name &&
								format
									.lower(el.name)
									.indexOf(format.lower(part)) >= 0) ||
							(el.listSuffix &&
								format
									.lower(String(el.listSuffix))
									.indexOf(format.lower(part)) >= 0),
					);
					if (sub.length > 0) {
						const m = sub[0];
						const id = String(m.value);
						if (seen.has(id)) return;
						next.push(id);
						seen.add(id);
						return;
					}
					if (seen.has(part)) return;
					next.push(part);
					seen.add(part);
				};
				parts.forEach(addPart);
				if (next.length === this.values.length) {
					return;
				}
				if (this.$refs['multi-input']) {
					this.$refs['multi-input'].focus();
				}
				this.$emit('change', this.formKey, next.join(','), q, null);
				return;
			}
			const result = [...this.values];
			if (e.target.value) result.push(e.target.value);
			this.multiSelectValue = '';

			const match = this.options.filter(el => (
				format.lower(el.text)?.includes(format.lower(e.target.value)) ||
				format.lower(el.name)?.includes(format.lower(e.target.value))
			));
			const alreadySelected = this.values.some(v => v === e.target.value);
			if (match?.length > 0 && !alreadySelected) {
				this.$emit('change', this.formKey, this.value, e.target.value, match[0])
			} else {
				this.$emit('change', this.formKey, result.join(','), e.target.value)
			}
		},
		setSearchResult(option, value) {
			if (value) {
				if (option.text && format.lower(option?.text).indexOf(format.lower(value)) >= 0) return true;
				if (option.sub && format.lower(option?.sub).indexOf(format.lower(value)) >= 0) return true;
				if (option.listSuffix && format.lower(String(option.listSuffix)).indexOf(format.lower(value)) >= 0) return true;
				return false;
			} else {
				return true;
			}
		},

		// ---------- 날짜 (리팩토링 핵심) ----------
		// 달력 선택 시: 항상 YYYY-MM-DD로 emit
		onDatepickerInput(val) {
			const iso = this.toHyphen(val);
			this.displayMode = 'hyphen';
			this.isTyping = false;
			this.$emit('input', iso || '');
		},
		onDatepickerContext(ctx) {
			const iso = this.toHyphen(ctx && ctx.selectedYMD);
			this.displayMode = 'hyphen';
			this.isTyping = false;
			if (iso) this.$emit('input', iso);
		},
		// 텍스트 수동 입력: 숫자만 유지, 8자리면 그대로 emit(YYYYMMDD)
		onDateTextInput(raw) {
			this.isTyping = true;
			this.displayMode = 'digits';

			const digits = this.onlyDigits(raw).slice(0, 8);
			this.dateDraft = digits;
			if (digits.length === 8) {
				this.$emit('input', digits); 
				this.isTyping = false;
				this.displayMode = 'hyphen';
				this.dateDraft = '';
			}
		},
		onlyDigits(v) {
			return String(v == null ? '' : v).replace(/[^\d]/g, '');
		},
		toHyphen(v) {
			const s = String(v == null ? '' : v);
			if (!s) return '';
			if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;          // 이미 하이픈
			const d = this.onlyDigits(s);
			if (d.length === 8) return `${d.slice(0,4)}-${d.slice(4,6)}-${d.slice(6,8)}`; // 숫자 8자리 → 하이픈
			const out = this.format?.date?.({ date: s, onlyDate: true });
			return out || '';
		},
		onUnitInput(e) {
			this.$emit('changeUnit', e.target.value);
		},
	}
}
</script>

<style lang="scss" scoped>
.date-close-btn {
	position: absolute;
	top: 50%;
	right: 10px;
	transform: translateY(-50%);
	width: 20px;
	z-index: 98;
	cursor: pointer;
}
.disabled {
	color: #666 !important;
	pointer-events: none;
	cursor: default;
	:deep(*) {
		color: inherit;
		cursor: default;
	}
}
.unit {
	width: 30px;
	border-left: 1px solid #ccc;
	padding-left: 5px;
	display: inline-flex;
	border-radius: unset !important;
}
</style>
